'use client';

import { useState } from 'react';

import { logger } from '@/lib/logger.client';
import { ICoupon } from '@/lib/schemas/Coupon.zod';
import { couponService } from '@/services';

// Currency formatter utility
const toCurrencyFormat = (value: number, currency: string = 'ARS') => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency,
  }).format(Number(value));
};

type CartCouponProps = {
  appliedCoupon: ICoupon | null;
  onCouponApplied: (coupon: ICoupon) => void;
  onCouponRemoved: () => void;
  discount: number;
};

export const CartCoupon: React.FC<CartCouponProps> = ({ appliedCoupon, onCouponApplied, onCouponRemoved, discount }) => {
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Por favor, ingresa un código de cupón');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const result = await couponService.validateCoupon(couponCode.trim());

      if (result.valid && result.coupon) {
        onCouponApplied(result.coupon as ICoupon);
        setCouponCode('');
        logger.info({ couponCode }, 'Coupon applied successfully');
      } else {
        setCouponError(result.message || 'Cupón inválido o expirado');
      }
    } catch (error) {
      logger.error({ error }, 'Failed to apply coupon');
      setCouponError('Error al validar el cupón');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    onCouponRemoved();
    setCouponCode('');
    setCouponError('');
  };

  if (appliedCoupon) {
    return (
      <div className="mb-4">
        <div className="flex justify-between items-center p-3 bg-success/10 border border-success/20 rounded-lg">
          <div className="flex-1">
            <p className="font-semibold text-success">{appliedCoupon.code}</p>
            {appliedCoupon.description && <p className="text-sm text-gray-600">{appliedCoupon.description}</p>}
          </div>
          <button onClick={removeCoupon} data-testid="coupon-remove-button" className="ml-2 text-error hover:bg-error/10 p-1 rounded transition-colors" aria-label="Remover cupón">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex justify-between mt-2 text-success font-medium">
          <span>Descuento:</span>
          <span>-{toCurrencyFormat(discount)}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          placeholder="Código de cupón"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          disabled={isApplyingCoupon}
          data-testid="coupon-input"
        />
        <button
          onClick={applyCoupon}
          disabled={isApplyingCoupon || !couponCode.trim()}
          data-testid="coupon-apply-button"
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {isApplyingCoupon ? 'Validando...' : 'Aplicar'}
        </button>
      </div>
      {couponError && <p data-testid="coupon-error" className="text-sm text-error mt-1">{couponError}</p>}
    </div>
  );
};

export default CartCoupon;
